import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { queryKeys } from '../../api/queries/keys';
import { isActive, type UploadStatus } from '../../api/queries/uploads';

interface Polled {
  id: string;
  fileName: string;
  status: UploadStatus;
}

/**
 * What changed between two polls, for screen readers: each upload that was queued or
 * processing and has now finished (pc-of9.11 #18). An upload not seen before isn't
 * announced: it's new on screen, not a change.
 */
export function finishedUploads(
  before: ReadonlyMap<string, UploadStatus>,
  now: readonly Polled[],
): Polled[] {
  return now.filter((upload) => {
    const was = before.get(upload.id);
    return !!was && isActive(was) && !isActive(upload.status);
  });
}

/** "DISH.xlsx completed. SOLAR.xlsx was rejected." */
export const finishedMessage = (finished: readonly Polled[]) =>
  finished
    .map((upload) =>
      upload.status === 'completed'
        ? `${upload.fileName} completed.`
        : `${upload.fileName} was rejected.`,
    )
    .join(' ');

/**
 * The announcement for a polled list of uploads (render it in a polite status region),
 * and a refresh of what a completed upload changes: the pay dates, pay views and nav
 * counts.
 */
export function useStatusAnnouncer(uploads: readonly Polled[] | undefined) {
  const queryClient = useQueryClient();
  const seen = useRef(new Map<string, UploadStatus>());
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!uploads) return;
    const finished = finishedUploads(seen.current, uploads);
    for (const upload of uploads) seen.current.set(upload.id, upload.status);
    if (finished.length === 0) return;
    setMessage(finishedMessage(finished));
    if (finished.some((upload) => upload.status === 'completed')) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUploads() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.pay() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.navCounts() });
    }
  }, [uploads, queryClient]);

  return message;
}
